import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listNodes,
  getNode,
  getNodeWithRelationships,
  getPMNData,
  createNode,
  updateNode,
  deleteNode,
  softDeleteNode,
  restoreNode,
  upsertNode,
  createEdge,
  deleteEdge,
  softDeleteEdge,
  restoreEdge,
  searchNodes,
  globalSearch,
  listEdges,
  getNeighbors,
  getAllNeighbors,
  traverseBFS,
  getNodeDegree,
  getEdgeTypeDistribution,
  bulkCreateNodes,
  bulkUpdateNodes,
  bulkDeleteNodes,
  bulkCreateEdges,
  bulkDeleteEdges,
  searchNodesFullText,
  isGraphError,
  getErrorMessage,
  createPermissionError,
} from '@lingaai/supabase-client';
import type { GraphSearchResult } from '@lingaai/supabase-client';
import type { BulkCreateResult } from '@lingaai/supabase-client';
import type { Node, Edge, CreateNodeInput, UpdateNodeInput, CreateEdgeInput, PaginatedResponse } from '@lingaai/types';
import { useGraphPermissions } from './GraphPermissionsContext';

// ============================================
// CACHE INVALIDATION STRATEGY
// ============================================
//
// This file uses a HYBRID APPROACH for optimal performance and UX:
//
// 1. OPTIMISTIC UPDATES
//    - Immediately update the cache with new/changed data
//    - Provides instant UI feedback (no loading spinners)
//    - Zero network overhead for cache updates
//
// 2. ACTIVE INVALIDATION
//    - Invalidate only ACTIVE queries (visible/mounted components)
//    - Background/inactive queries are NOT refetched
//    - Ensures fresh data on next mount
//
// WHY HYBRID?
// - Instant UX: Users see changes immediately
// - Multi-user safe: Active invalidation ensures eventual consistency
// - Efficient: Avoids refetching hidden tabs or paginated pages
//
// EXAMPLE:
// User creates 1 Person
//   → Cache updated instantly (optimistic)
//   → Only visible lists refetch (active invalidation)
//   → Hidden tabs/pages don't refetch (saved ~90% of requests)
//
// ============================================
// NODE HOOKS
// ============================================

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Default error handler for mutations
 * Logs errors and can trigger toast notifications
 */
function handleMutationError(error: unknown, operation: string) {
  console.error(`Graph ${operation} failed:`, error);

  // Log structured error for monitoring
  if (isGraphError(error)) {
    console.error('Error details:', error.toJSON());
  }

  // Get user-friendly error message
  const userMessage = getErrorMessage(error);
  console.error('User message:', userMessage);

  // TODO: Trigger toast notification
  // toast.error(userMessage);
}

// ============================================
// QUERY HOOKS
// ============================================

/**
 * Hook to list nodes with pagination
 */
export function useNodes(
  type: string,
  filters?: {
    search?: string;
    includeDeleted?: boolean;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    filterModel?: import('@lingaai/types').FilterModel;
    includes?: Array<{
      edgeType: string;
      direction: 'outgoing' | 'incoming';
      nodeTypes: string[];
      as: string;
      includeEdgeProps?: boolean;
      edgeFilter?: Record<string, unknown>;
    }>;
  }
) {
  return useQuery({
    queryKey: ['nodes', type, filters],
    queryFn: () => listNodes(type, filters),
  });
}

/**
 * Hook to get a single node by ID
 */
export function useNode(id: string | undefined) {
  return useQuery({
    queryKey: ['node', id],
    queryFn: () => getNode(id!),
    enabled: !!id,
  });
}

/**
 * Hook to get a node with all its relationships
 */
export function useNodeWithRelationships(id: string | undefined, includeDeleted?: boolean) {
  return useQuery({
    queryKey: ['node-with-relationships', id, includeDeleted],
    queryFn: () => getNodeWithRelationships(id!),
    enabled: !!id,
  });
}

/**
 * Hook to get PMN data (Properties, Metrics, Narrative) for a node
 * Optimized to fetch all 3 edge types in a single query instead of 6 queries
 */
export function usePMNData(nodeId: string | undefined) {
  return useQuery({
    queryKey: ['pmn-data', nodeId],
    queryFn: () => getPMNData(nodeId!),
    enabled: !!nodeId,
  });
}

/**
 * Hook to create a new node
 * Uses hybrid approach: optimistic updates + active invalidation
 *
 * If GraphPermissionsProvider is available, performs frontend permission check
 * before calling the backend (with skipModuleCheck: true for performance).
 * Otherwise, backend performs the check (with database constraint as final security layer).
 */
export function useCreateNode() {
  const queryClient = useQueryClient();
  const permissions = useGraphPermissions();

  return useMutation({
    mutationFn: (input: CreateNodeInput) => {
      // Frontend permission check (if provider available)
      if (permissions && !permissions.isNodeTypeAllowed(input.type)) {
        createPermissionError('node', input.type);
      }

      // Skip backend check if frontend already validated (performance optimization)
      // Database constraint still provides security layer regardless
      return createNode(input, { skipModuleCheck: !!permissions });
    },
    onSuccess: (node: Node) => {
      // 1. Optimistic update: Insert new node into all cached lists
      // This provides instant UI feedback without network request
      queryClient.setQueriesData({ queryKey: ['nodes', node.type] }, (old: PaginatedResponse<Node> | undefined) => {
        if (!old) return old;

        return {
          ...old,
          data: [node, ...old.data],
          pagination: {
            ...old.pagination,
            total: old.pagination.total + 1,
          },
        };
      });

      // 2. Invalidate active queries only (not background/inactive ones)
      // Ensures fresh data on next mount while avoiding unnecessary refetches
      queryClient.invalidateQueries({
        queryKey: ['nodes', node.type],
        refetchType: 'active',
      });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'create node');
    },
  });
}

/**
 * Hook to update a node
 * Uses hybrid approach: optimistic updates + active invalidation
 */
export function useUpdateNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates, nodeType }: { id: string; updates: UpdateNodeInput; nodeType?: string }) => updateNode(id, updates, nodeType),
    onSuccess: (node: Node) => {
      // 1. Update specific node cache
      queryClient.setQueryData(['node', node.id], node);

      // 2. Optimistically update node in all list caches
      // This ensures instant UI update across all lists showing this node
      queryClient.setQueriesData({ queryKey: ['nodes', node.type] }, (old: PaginatedResponse<Node> | undefined) => {
        if (!old) return old;

        return {
          ...old,
          data: old.data.map((n) => (n.id === node.id ? node : n)),
        };
      });

      // 3. Invalidate relationships (can't optimistically update - complex data)
      queryClient.invalidateQueries({
        queryKey: ['node-with-relationships', node.id],
        refetchType: 'active',
      });

      // 4. Invalidate PMN data (complex nested data structure)
      queryClient.invalidateQueries({
        queryKey: ['pmn-data', node.id],
        refetchType: 'active',
      });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'update node');
    },
  });
}

/**
 * Hook to soft delete a node
 * Uses hybrid approach: optimistic updates + active invalidation
 */
export function useSoftDeleteNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => softDeleteNode(id),
    onSuccess: (node: Node) => {
      // 1. Remove from specific node cache
      queryClient.removeQueries({ queryKey: ['node', node.id] });

      // 2. Optimistically remove from all list caches
      // This provides instant feedback without waiting for refetch
      queryClient.setQueriesData({ queryKey: ['nodes', node.type] }, (old: PaginatedResponse<Node> | undefined) => {
        if (!old) return old;

        return {
          ...old,
          data: old.data.filter((n) => n.id !== node.id),
          pagination: {
            ...old.pagination,
            total: Math.max(0, old.pagination.total - 1),
          },
        };
      });

      // 3. Invalidate active queries as fallback
      queryClient.invalidateQueries({
        queryKey: ['nodes', node.type],
        refetchType: 'active',
      });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'soft delete node');
    },
  });
}

/**
 * Hook to permanently delete a node
 */
export function useDeleteNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteNode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'delete node');
    },
  });
}

/**
 * Hook to restore a soft-deleted node
 * Uses hybrid approach: optimistic updates + active invalidation
 */
export function useRestoreNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => restoreNode(id),
    onSuccess: (node: Node) => {
      // 1. Update specific node cache
      queryClient.setQueryData(['node', node.id], node);

      // 2. Optimistically add restored node back to lists
      queryClient.setQueriesData({ queryKey: ['nodes', node.type] }, (old: PaginatedResponse<Node> | undefined) => {
        if (!old) return old;

        // Check if node already exists in list
        const exists = old.data.some((n) => n.id === node.id);
        if (exists) return old;

        return {
          ...old,
          data: [node, ...old.data],
          pagination: {
            ...old.pagination,
            total: old.pagination.total + 1,
          },
        };
      });

      // 3. Invalidate active queries
      queryClient.invalidateQueries({
        queryKey: ['nodes', node.type],
        refetchType: 'active',
      });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'restore node');
    },
  });
}

/**
 * Hook to upsert a node (insert or update based on natural key)
 * Uses hybrid approach: optimistic updates + active invalidation
 */
export function useUpsertNode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateNodeInput) => upsertNode(input),
    onSuccess: (node: Node) => {
      // 1. Update specific node cache
      queryClient.setQueryData(['node', node.id], node);

      // 2. Optimistically upsert in lists (create or update)
      queryClient.setQueriesData({ queryKey: ['nodes', node.type] }, (old: PaginatedResponse<Node> | undefined) => {
        if (!old) return old;

        const existingIndex = old.data.findIndex((n) => n.id === node.id);

        if (existingIndex >= 0) {
          // Update existing node
          return {
            ...old,
            data: old.data.map((n) => (n.id === node.id ? node : n)),
          };
        } else {
          // Insert new node
          return {
            ...old,
            data: [node, ...old.data],
            pagination: {
              ...old.pagination,
              total: old.pagination.total + 1,
            },
          };
        }
      });

      // 3. Invalidate active queries
      queryClient.invalidateQueries({
        queryKey: ['nodes', node.type],
        refetchType: 'active',
      });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'upsert node');
    },
  });
}

/**
 * Hook to search nodes across types
 */
export function useSearchNodes(
  query: string,
  options?: {
    types?: string[];
    includeDeleted?: boolean;
    limit?: number;
  }
) {
  return useQuery({
    queryKey: ['search-nodes', query, options],
    queryFn: () => searchNodes(query, options),
    enabled: query.length > 0,
  });
}

/**
 * Hook for global search across all node types
 */
export function useGlobalSearch(
  query: string,
  options?: {
    page?: number;
    pageSize?: number;
  }
) {
  return useQuery<GraphSearchResult[]>({
    queryKey: ['global-search', query, options],
    queryFn: () => globalSearch(query, options),
    enabled: query.length >= 2,
  });
}

// ============================================
// EDGE HOOKS
// ============================================

/**
 * Hook to list edges
 */
export function useEdges(
  type: string,
  filters?: {
    src?: string;
    dst?: string;
    includeDeleted?: boolean;
  }
) {
  return useQuery({
    queryKey: ['edges', type, filters],
    queryFn: () => listEdges(type, filters),
  });
}

/**
 * Hook to create a new edge
 *
 * If GraphPermissionsProvider is available, performs frontend permission check
 * before calling the backend (with skipModuleCheck: true for performance).
 * Otherwise, backend performs the check (with database constraint as final security layer).
 */
export function useCreateEdge() {
  const queryClient = useQueryClient();
  const permissions = useGraphPermissions();

  return useMutation({
    mutationFn: (input: CreateEdgeInput) => {
      // Frontend permission check (if provider available)
      if (permissions && !permissions.isEdgeTypeAllowed(input.type)) {
        createPermissionError('edge', input.type);
      }

      // Skip backend check if frontend already validated (performance optimization)
      // Database constraint still provides security layer regardless
      return createEdge(input, { skipModuleCheck: !!permissions });
    },
    onSuccess: (edge: Edge) => {
      // Invalidate relationship queries for both connected nodes (active only)
      queryClient.invalidateQueries({
        queryKey: ['node-with-relationships', edge.src],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['node-with-relationships', edge.dst],
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['edges'],
        refetchType: 'active',
      });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'create edge');
    },
  });
}

/**
 * Hook to soft delete an edge
 */
export function useSoftDeleteEdge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => softDeleteEdge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['node-with-relationships'] });
      queryClient.invalidateQueries({ queryKey: ['edges'] });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'soft delete edge');
    },
  });
}

/**
 * Hook to permanently delete an edge
 */
export function useDeleteEdge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteEdge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['node-with-relationships'] });
      queryClient.invalidateQueries({ queryKey: ['edges'] });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'delete edge');
    },
  });
}

/**
 * Hook to restore a soft-deleted edge
 */
export function useRestoreEdge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => restoreEdge(id),
    onSuccess: (edge) => {
      queryClient.invalidateQueries({ queryKey: ['node-with-relationships', edge.src] });
      queryClient.invalidateQueries({ queryKey: ['node-with-relationships', edge.dst] });
      queryClient.invalidateQueries({ queryKey: ['edges'] });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'restore edge');
    },
  });
}

// ============================================
// ADVANCED GRAPH TRAVERSAL HOOKS
// ============================================

/**
 * Hook to get neighbors by edge type
 */
export function useNeighbors(nodeId: string | undefined, edgeType: string, direction: 'outgoing' | 'incoming' | 'both' = 'both') {
  return useQuery({
    queryKey: ['neighbors', nodeId, edgeType, direction],
    queryFn: () => getNeighbors(nodeId!, edgeType, direction),
    enabled: !!nodeId,
  });
}

/**
 * Hook to get all neighbors regardless of edge type
 */
export function useAllNeighbors(nodeId: string | undefined, direction: 'outgoing' | 'incoming' | 'both' = 'both') {
  return useQuery({
    queryKey: ['all-neighbors', nodeId, direction],
    queryFn: () => getAllNeighbors(nodeId!, direction),
    enabled: !!nodeId,
  });
}

/**
 * Hook to traverse graph with BFS
 */
export function useTraverseBFS(startNodeId: string | undefined, maxDepth: number = 3, edgeTypeFilter?: string) {
  return useQuery({
    queryKey: ['traverse-bfs', startNodeId, maxDepth, edgeTypeFilter],
    queryFn: () => traverseBFS(startNodeId!, maxDepth, edgeTypeFilter),
    enabled: !!startNodeId,
  });
}

/**
 * Hook to get node degree
 */
export function useNodeDegree(nodeId: string | undefined) {
  return useQuery({
    queryKey: ['node-degree', nodeId],
    queryFn: () => getNodeDegree(nodeId!),
    enabled: !!nodeId,
  });
}

/**
 * Hook to get edge type distribution for a node
 */
export function useEdgeTypeDistribution(nodeId: string | undefined) {
  return useQuery({
    queryKey: ['edge-type-distribution', nodeId],
    queryFn: () => getEdgeTypeDistribution(nodeId!),
    enabled: !!nodeId,
  });
}

// ============================================
// BULK OPERATION HOOKS
// ============================================

/**
 * Hook to bulk create nodes
 */
export function useBulkCreateNodes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputs: CreateNodeInput[]) => bulkCreateNodes(inputs),
    onSuccess: (result: BulkCreateResult<Node>) => {
      // Invalidate queries for all created node types
      const types = new Set(result.created.map((node: Node) => node.type));
      types.forEach((type) => {
        queryClient.invalidateQueries({ queryKey: ['nodes', type] });
      });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'bulk create nodes');
    },
  });
}

/**
 * Hook to bulk update nodes
 */
export function useBulkUpdateNodes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: Array<{ id: string; props: Record<string, unknown> }>) => bulkUpdateNodes(updates),
    onSuccess: (result: BulkCreateResult<Node>) => {
      // Invalidate queries for all updated nodes
      result.created.forEach((node: Node) => {
        queryClient.invalidateQueries({ queryKey: ['node', node.id] });
        queryClient.invalidateQueries({ queryKey: ['node-with-relationships', node.id] });
        queryClient.invalidateQueries({ queryKey: ['nodes', node.type] });
      });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'bulk update nodes');
    },
  });
}

/**
 * Hook to bulk delete nodes
 */
export function useBulkDeleteNodes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeIds, soft = true }: { nodeIds: string[]; soft?: boolean }) => bulkDeleteNodes(nodeIds, soft),
    onSuccess: () => {
      // Invalidate all node queries
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['node'] });
      queryClient.invalidateQueries({ queryKey: ['node-with-relationships'] });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'bulk delete nodes');
    },
  });
}

/**
 * Hook to bulk create edges
 */
export function useBulkCreateEdges() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputs: CreateEdgeInput[]) => bulkCreateEdges(inputs),
    onSuccess: (result: BulkCreateResult<Edge>) => {
      // Invalidate queries for all affected nodes
      const nodeIds = new Set<string>();
      result.created.forEach((edge: Edge) => {
        nodeIds.add(edge.src);
        nodeIds.add(edge.dst);
      });
      nodeIds.forEach((nodeId) => {
        queryClient.invalidateQueries({ queryKey: ['node-with-relationships', nodeId] });
      });
      queryClient.invalidateQueries({ queryKey: ['edges'] });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'bulk create edges');
    },
  });
}

/**
 * Hook to bulk delete edges
 */
export function useBulkDeleteEdges() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ edgeIds, soft = true }: { edgeIds: string[]; soft?: boolean }) => bulkDeleteEdges(edgeIds, soft),
    onSuccess: () => {
      // Invalidate all edge queries
      queryClient.invalidateQueries({ queryKey: ['edges'] });
      queryClient.invalidateQueries({ queryKey: ['node-with-relationships'] });
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'bulk delete edges');
    },
  });
}

// ============================================
// ENHANCED SEARCH HOOKS
// ============================================

/**
 * Hook for full-text search across nodes
 */
export function useSearchNodesFullText(
  query: string,
  options?: {
    nodeTypes?: string[];
    page?: number;
    pageSize?: number;
    includeDeleted?: boolean;
  }
) {
  return useQuery({
    queryKey: ['search-nodes-full-text', query, options],
    queryFn: () => searchNodesFullText(query, options),
    enabled: query.length > 0,
  });
}
